import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { AssetUrls } from 'config/assets';
import { SEOHelmet, StructuredData, BreadcrumbList } from 'components/SEO';
import { useGetPublishedArticlesQuery } from 'store/services/blog/blogApi';
import './ArticlesHub.css';

interface Article {
  title: string;
  description: string;
  readTime: string;
  hero: string;
  route: string;
  featured?: boolean;
  alt: string;
  buttonText?: string;
}

const ARTICLES: Article[] = [
  {
    title: 'How TradeMagic Works',
    description: 'A complete quick-start guide with everything you need to know about what makes TradeMagic special.',
    readTime: '3 MINS',
    hero: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/login/login-background.png',
    route: '/articles/how-it-works',
    featured: true,
    alt: 'TradeMagic background with magical cards'
  },
  {
    title: 'Shipping Guide',
    description: 'Instructions for how to mail Magic cards safely and easily.',
    readTime: '3 MINS',
    hero: AssetUrls.articles.shippingGuide,
    route: '/articles/shipping',
    alt: 'Blue-themed wizard character with shipping supplies',
    buttonText: 'Read'
  },
  {
    title: 'Sustainable Economics',
    description: 'How we manage the long-term health of TradeMagic.',
    readTime: '3 MINS',
    hero: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/articles/hero_sensei_divining_top.png',
    route: '/articles/sustainable-economics',
    alt: 'Sensei\'s Divining Top card representing economic management',
    buttonText: 'Read'
  },
  {
    title: 'Condition Guide',
    description: 'Instructions for how to evaluate the condition of Magic cards.',
    readTime: '3 MINS',
    hero: AssetUrls.articles.conditionGuide,
    route: '/articles/condition-guide',
    alt: 'Futuristic crystalline city representing card quality standards',
    buttonText: 'Read'
  }
];

const ArticlesHub = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Fetch CMS articles
  const { data: cmsArticlesData } = useGetPublishedArticlesQuery({
    page: 1,
    limit: 100,
    sortBy: 'publishedAt',
    sortOrder: 'desc',
  });

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle sidebar navigation attempts
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  // Merge hardcoded articles with CMS articles
  const cmsArticles: Article[] = (cmsArticlesData?.data || []).map((article: any) => ({
    title: article.title,
    description: article.subtitle || '',
    readTime: '', // CMS articles don't have read time
    hero: article.heroImageUrl || '',
    route: `/articles/${article.slug}`,
    alt: article.title,
    buttonText: 'Read',
  }));

  const allArticles = [...ARTICLES, ...cmsArticles];

  const featuredArticle = allArticles.find(article => article.featured);
  const regularArticles = allArticles.filter(article => !article.featured);

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="Articles & Resources - TradeMagic"
        description="Learn how to trade Magic cards on TradeMagic. Guides on card conditions, shipping, and sustainable trading economics."
        canonical="https://trademagic.gg/articles"
        keywords="MTG trading guides, Magic card condition guide, MTG shipping guide, trade Magic cards, TradeMagic resources"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Articles', url: 'https://trademagic.gg/articles' },
        ]}
      />

      {/* CollectionPage Schema */}
      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Helpful Articles",
          "description": "Your HQ for TradeMagic resources",
          "url": "https://trademagic.gg/articles",
          "publisher": {
            "@type": "Organization",
            "name": "TradeMagic",
            "url": "https://trademagic.gg"
          }
        }}
      />

      <div className="articles-hub-layout">

      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="articles-hub-mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="articles-hub-content-wrapper">
        <LeftNavigationBar
          currentTab={{ path: '/articles', Component: <></> }}
          isPublicMode={!currentUser}
          onNavigationAttempt={handleNavigationAttempt}
          isAuthenticated={!!currentUser}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
        />

        <div className="articles-hub-main">
          {/* Top Navigation Bar */}
          <TopNavigationBar
            title="Articles"
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />

          <div className="articles-hub-container">
            {/* Page Header */}
            <header className="articles-hub-header">
              <h1 className="articles-hub-title">Helpful Articles</h1>
              <h2 className="articles-hub-subtitle">Your HQ for TradeMagic resources</h2>
            </header>

            {/* Featured Article */}
            {featuredArticle && (
              <section className="articles-hub-featured-section">
                <article
                  className="articles-hub-card articles-hub-card-featured"
                  onClick={() => handleCardClick(featuredArticle.route)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleCardClick(featuredArticle.route);
                    }
                  }}
                >
                  <div
                    className="articles-hub-card-hero"
                    style={{ backgroundImage: `url(${featuredArticle.hero})` }}
                  >
                    <div className="articles-hub-card-overlay" />
                    <img
                      src={featuredArticle.hero}
                      alt={featuredArticle.alt}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div className="articles-hub-card-content">
                    <div className="articles-hub-card-header">
                      <h3 className="articles-hub-card-title">{featuredArticle.title}</h3>
                      <span className="articles-hub-card-read-time">{featuredArticle.readTime}</span>
                    </div>
                    <p className="articles-hub-card-description">{featuredArticle.description}</p>
                    <button className="articles-hub-card-button">{featuredArticle.buttonText || 'Learn More'}</button>
                  </div>
                </article>
              </section>
            )}

            {/* Article Grid */}
            <section className="articles-hub-grid">
              {regularArticles.map((article) => (
                <article
                  key={article.route}
                  className="articles-hub-card"
                  onClick={() => handleCardClick(article.route)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleCardClick(article.route);
                    }
                  }}
                >
                  <div
                    className="articles-hub-card-hero"
                    style={{ backgroundImage: `url(${article.hero})` }}
                  >
                    <div className="articles-hub-card-overlay" />
                    <img
                      src={article.hero}
                      alt={article.alt}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div className="articles-hub-card-content">
                    <div className="articles-hub-card-header">
                      <h3 className="articles-hub-card-title">{article.title}</h3>
                      <span className="articles-hub-card-read-time">{article.readTime}</span>
                    </div>
                    <p className="articles-hub-card-description">{article.description}</p>
                    <button className="articles-hub-card-button">{article.buttonText || 'Learn More'}</button>
                  </div>
                </article>
              ))}
            </section>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />

      <Footer />
      </div>
    </>
  );
};

export default ArticlesHub;
